import ArrowDown from "@mui/icons-material/ArrowDownward";
import ArrowUp from "@mui/icons-material/ArrowUpward";
import { Button, ButtonGroup } from "@mui/material";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import type { RemoteServiceErrorResponse } from "core/entities/CentrixTypes";
import { useBrcyCode } from "core/hooks/useBrcyCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { useLocale } from "core/hooks/useLocale";
import { useToaster } from "core/hooks/useToaster";
import { StyledDivider } from "core/pages/AdvertisementBannerManagement/StyledDivider";
import { sortObjectsBasedOnReferenceOrder } from "core/utils/sortObjectsBasedOnReferenceOrder";
import { viewTransition } from "core/utils/viewTransition";
import Image from "next/image";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { sortAndSwap } from "../../utils/sortAndSwap";
import { ApiErrorDisplayAccordion } from "../ApiErrorDisplayAccordion";
import { DialogFormContainer } from "../DialogFormContainer";
import { MoveIcon } from "../icons/MoveIcon";
import { LinkButton } from "../LinkButton";
import { ReactHookNumberInput } from "../ReactHookFormComponents/ReactHookNumberInput";

const defaultValues = {
  order: [] as string[],
  intervalInMilliseconds: 6000,
};

export function AdvertisementWidgetSettingsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const locale = useLocale();
  const brcy = useBrcyCode();
  const { data, isError, isLoading, mutate } = useCentrixApi({
    path: "/api/app/advertisement-banner/public-list",
    parameters: {
      query: {
        Locale: locale,
        BrcyCode: brcy,
      },
    },
    fetchAll: true,
  });
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:AdvertisementWidgetSettingsDialog",
  });
  const centrixFetch = useCentrixFetch();
  const values = useMemo(() => {
    return {
      order: data?.widgetSettings?.orderedBannerIds ?? defaultValues.order,
      intervalInMilliseconds:
        data?.widgetSettings?.intervalInMilliseconds ??
        defaultValues.intervalInMilliseconds,
    };
  }, [data]);
  const formMethods = useForm({
    defaultValues,
    values,
  });
  const [error, setError] = useState<RemoteServiceErrorResponse | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  const { isSubmitting } = formMethods.formState;
  const { successToast } = useToaster();
  const successMessage = t(
    "successToast",
    "Advertisement widget settings saved successfully."
  );
  const order = formMethods.watch("order");

  const handleSubmit = formMethods.handleSubmit(async (data) => {
    const res = await centrixFetch({
      method: "put",
      path: "/api/app/advertisement-banner/widget-settings",
      body: {
        brcyCode: brcy,
        orderedBannerIds: data.order,
        intervalInMilliseconds: data.intervalInMilliseconds,
      },
    });
    if (!res.ok) {
      const error = await res.json();
      setError(error);
      return;
    }
    await res.json(); // Consume response
    await mutate();
    successToast(successMessage);
    onClose();
  });

  const validImages =
    data?.items?.filter(
      (entry): entry is Exclude<typeof entry, null | undefined> =>
        Boolean(entry?.id && entry?.imageSrc && entry?.altText && entry?.title)
    ) ?? [];
  const imageIds = validImages.map((image) => image.id);
  const images = sortObjectsBasedOnReferenceOrder(validImages, order, "id");

  function setOrder(
    newOrderOrFunc: string[] | ((currentOrder: string[]) => string[])
  ) {
    viewTransition(() => {
      formMethods.setValue(
        "order",
        typeof newOrderOrFunc === "function"
          ? newOrderOrFunc(formMethods.getValues("order"))
          : newOrderOrFunc
      );
    });
  }

  function handleUp(index: number) {
    setOrder((currentOrder) =>
      sortAndSwap(imageIds, currentOrder, index, index - 1)
    );
  }

  function handleDown(index: number) {
    setOrder((currentOrder) =>
      sortAndSwap(imageIds, currentOrder, index, index + 1)
    );
  }

  function swapIds(a: string, b: string) {
    setOrder((currentOrder) =>
      sortAndSwap(
        imageIds,
        currentOrder,
        currentOrder.indexOf(a),
        currentOrder.indexOf(b)
      )
    );
  }

  return (
    <DialogFormContainer
      onSubmit={handleSubmit}
      open={open}
      onDialogClose={onClose}
      title={t("dialogTitle", "Advertisement Widget Settings")}
      onCancelButtonClick={onClose}
      isSubmitting={isSubmitting}
      scroll="body"
      onResetButtonClick={() => {
        viewTransition(() => {
          formMethods.reset(values);
        });
      }}
      isError={isError}
      isLoading={isLoading}
    >
      <Stack direction="row" justifyContent="flex-end" alignItems="center">
        <LinkButton href="/manage/advertisement-banner" size="small">
          {t("manageBannersLinkText", "Go To Manage")}
        </LinkButton>
      </Stack>
      <ReactHookNumberInput
        name="intervalInMilliseconds"
        sx={{ my: 2 }}
        fullWidth
        control={formMethods.control}
        label={t("intervalInMillisecondsLabel", "Interval in Milliseconds")}
      />
      <StyledDivider textAlign="left">
        {t("imageOrderTitle", "Image Order")}
      </StyledDivider>
      <Stack
        gap={2}
        mx="auto"
        sx={{ "& > *": { viewTransitionName: "match-element" } }}
      >
        {images.map((image, index, { length }) => {
          return (
            <Box
              key={image.id}
              display="flex"
              mx="auto"
              gap={1}
              draggable
              onDragStart={() => {
                setDragId(image.id);
              }}
              onDragEnd={(e) => {
                e.preventDefault();
                setDragId(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                if (dragId && dragId !== image.id) {
                  swapIds(dragId, image.id);
                }
              }}
            >
              <Paper
                sx={{
                  mx: "auto",
                  overflow: "hidden",
                  aspectRatio: "3 / 1",
                  height: "auto",
                  position: "relative",
                  width: { xs: 150, sm: 300 },
                  cursor: "grab",
                }}
              >
                <Image
                  src={image.imageSrc}
                  alt={image.altText}
                  title={image.title}
                  fill
                  placeholder={image.imageBlurDataUrl ? "blur" : "empty"}
                  blurDataURL={image.imageBlurDataUrl}
                  style={{
                    objectFit: "cover",
                  }}
                />
              </Paper>
              {dragId === null ? (
                <ButtonGroup
                  orientation="vertical"
                  sx={{ "& > button": { flexGrow: 1 } }}
                  aria-label="Vertical button group"
                  size="small"
                >
                  <Button
                    variant="outlined"
                    disabled={index === 0}
                    onClick={() => {
                      handleUp(index);
                    }}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={index >= length - 1}
                    onClick={() => {
                      handleDown(index);
                    }}
                  >
                    <ArrowDown />
                  </Button>
                </ButtonGroup>
              ) : (
                <ButtonGroup
                  orientation="vertical"
                  sx={{ "& > button": { flexGrow: 1 } }}
                  aria-label="Vertical button group"
                  size="small"
                >
                  <Button variant="outlined">
                    <MoveIcon />
                  </Button>
                </ButtonGroup>
              )}
            </Box>
          );
        })}
      </Stack>
      <ApiErrorDisplayAccordion errorResponse={error} />
    </DialogFormContainer>
  );
}
